import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { intlMessage } from 'constants/propTypes';

import { Card } from 'components/Card';
import Message from 'components/Message';
import LoadingDots from 'components/LoadingDots';
import PageTurner from 'components/PageTurner';

// This component requires a header shaped like this:
// <tr>
//   <th>
//     content
//   </th>
//   <th>
//     more content
//   </th>
//   ...
// </tr>

// and children shaped like this:
// <tr>
//   <td>
//     content
//   </td>
//   <td>
//     more content
//   </td>
//   ...
// </tr>

// The HTML shape is actually important and the styling is lost / the table will misbehave if another shape is used

const Table = ({
  children,
  className,
  emptyMessage,
  header,
  isLoading,
  hasTopPageTurner,
  topPageTurnerOptionalContent,
  topPageTurnerRightSideContent,
  hasBottomPageTurner,
  bottomPageTurnerOptionalContent,
  changePage,
  count,
  entriesPerPage,
  page,
  sidePanel = null,
  hasFilters,
  filters,
}) => {
  const renderTableBody = () =>
    !isLoading && children && children.length ? (
      children
    ) : (
      <tr>
        <td colSpan={100} className="text--center">
          {!isLoading ? <Message message={emptyMessage} /> : <LoadingDots />}
        </td>
      </tr>
    );

  return (
    <div className={classNames('table-container', className)}>
      {hasTopPageTurner && (
        <PageTurner
          count={count}
          changePage={changePage}
          entriesPerPage={entriesPerPage}
          isLoading={isLoading}
          page={page}
          rightSideContent={topPageTurnerRightSideContent}
          className="mb-r">
          {topPageTurnerOptionalContent}
        </PageTurner>
      )}
      {hasFilters && <div className="table-filters">{filters}</div>}
      <div className="table-content flex items-start">
        <Card className="table-component" flush>
          <table>
            <thead>{header}</thead>
            <tbody>{renderTableBody()}</tbody>
          </table>
        </Card>
        {!!sidePanel && sidePanel}
      </div>
      {hasBottomPageTurner && (
        <PageTurner
          count={count}
          changePage={changePage}
          entriesPerPage={entriesPerPage}
          isLoading={isLoading}
          page={page}>
          {bottomPageTurnerOptionalContent}
        </PageTurner>
      )}
    </div>
  );
};

Table.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  emptyMessage: intlMessage,
  header: PropTypes.node,
  isLoading: PropTypes.bool,

  // used to control pagination
  hasTopPageTurner: PropTypes.bool,
  topPageTurnerOptionalContent: PropTypes.node,
  topPageTurnerRightSideContent: PropTypes.node,
  hasBottomPageTurner: PropTypes.bool,
  bottomPageTurnerOptionalContent: PropTypes.node,
  changePage: PropTypes.func,
  count: PropTypes.number,
  entriesPerPage: PropTypes.number,
  page: PropTypes.number,

  sidePanel: PropTypes.node,

  // Filters capability
  hasFilters: PropTypes.bool,
  filters: PropTypes.node,
};

export default Table;
